import React, { useState, useEffect, useCallback } from 'react';
import { getGmailTokens, setGmailTokens } from '../utils/gmail';
import { createActivityLogEntry } from '../api/monday';

const IS_PROD = process.env.NODE_ENV === 'production';

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  indigo:       '#534AB7',
  indigoDark:   '#3C3489',
  indigoLight:  '#EEEDFE',
  teal:         '#1D9E75',
  tealLight:    '#E1F5EE',
  amber:        '#633806',
  amberLight:   '#FAEEDA',
  red:          '#DC2626',
  redLight:     '#FEF2F2',
  redBorder:    '#FECACA',
  bgPage:       '#f5f4f1',
  bgCard:       '#ffffff',
  bgSoft:       '#f0efe9',
  border:       '#e8e6e0',
  borderSoft:   '#f3f2ef',
  textPrimary:  '#111827',
  textSecondary:'#6b7280',
  textTertiary: '#9ca3af',
};

const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    minHeight: '100vh', backgroundColor: C.bgPage,
    display: 'flex', flexDirection: 'column',
  },
  nav: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', backgroundColor: C.bgCard,
    borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, zIndex: 10,
  },
  backBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '20px', color: C.indigo, padding: '0 4px', lineHeight: 1, fontWeight: '600',
  },
  navTitle: { fontSize: '15px', fontWeight: '600', color: C.textPrimary, margin: 0, flex: 1 },
  navSub:   { fontSize: '12px', color: C.textTertiary },
  body: { flex: 1, padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    backgroundColor: C.bgCard, borderRadius: '14px',
    padding: '18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  label: {
    fontSize: '11px', fontWeight: '700', color: C.textTertiary,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: '6px', display: 'block',
  },
  input: {
    border: `1.5px solid ${C.border}`, borderRadius: '8px',
    padding: '10px 12px', fontSize: '14px', color: C.textPrimary,
    outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit', backgroundColor: C.bgCard,
  },
  textarea: {
    border: `1.5px solid ${C.border}`, borderRadius: '8px',
    padding: '10px 12px', fontSize: '14px', color: C.textPrimary,
    outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.6',
    minHeight: '200px', backgroundColor: C.bgCard,
  },
  btnPrimary: {
    width: '100%', padding: '14px',
    backgroundColor: C.teal, color: '#fff',
    border: 'none', borderRadius: '12px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnSecondary: {
    width: '100%', padding: '12px', backgroundColor: C.bgCard,
    color: C.textPrimary, border: `1px solid ${C.border}`,
    borderRadius: '12px', fontSize: '14px', fontWeight: '500',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnIndigo: {
    padding: '8px 16px', backgroundColor: C.indigoLight,
    color: C.indigo, border: `1px solid ${C.indigo}`,
    borderRadius: '8px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  error:   { background: C.redLight,   border: `1px solid ${C.redBorder}`, borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#991B1B' },
  warning: { background: C.amberLight, border: `1px solid #FCD34D`,        borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: C.amber },
  success: { background: C.tealLight,  border: `1px solid ${C.teal}`,      borderRadius: '10px', padding: '16px',      fontSize: '14px', color: '#085041', textAlign: 'center', fontWeight: '600' },
  loading: { textAlign: 'center', padding: '40px 20px', color: C.textTertiary, fontSize: '14px' },
  draftingBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', color: C.indigo, fontWeight: '600',
    padding: '4px 10px', backgroundColor: C.indigoLight,
    borderRadius: '20px',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────
function nextWeekStr() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// ── Gmail connect screen ───────────────────────────────────────────────────
function ConnectGmail({ brokerName, onCancel }) {
  function handleConnect() {
    const url = IS_PROD
      ? `/api/gmail-auth?broker=${encodeURIComponent(brokerName)}`
      : `/api/gmail-auth?broker=${encodeURIComponent(brokerName)}`;
    window.location.href = url;
  }

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={s.navTitle}>Connect Gmail</p>
      </div>
      <div style={s.body}>
        <div style={{ ...s.card, textAlign: 'center', padding: '32px 24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: C.textPrimary, margin: '0 0 10px' }}>
            Connect your Gmail
          </h2>
          <p style={{ fontSize: '14px', color: C.textSecondary, margin: '0 0 24px', lineHeight: '1.5' }}>
            You only need to do this once. Sterling CRE will send emails on your behalf from your own Gmail account.
          </p>
          <button
            onClick={handleConnect}
            style={{
              ...s.btnPrimary,
              backgroundColor: C.indigo,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            }}
          >
            <span style={{ fontSize: '18px' }}>G</span>
            Connect Gmail Account
          </button>
        </div>
        <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Main compose component ─────────────────────────────────────────────────
export default function EmailCompose({ contact, brokerName, onSent, onCancel }) {
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [guidance, setGuidance] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState(null);
  const [logError, setLogError] = useState(null);

  const tokens = getGmailTokens(brokerName);

  // ── Generate AI draft on mount ───────────────────────────────────────────
  const generateDraft = useCallback(async (currentGuidance) => {
    setDrafting(true);
    setError(null);
    try {
      const res = await fetch('/api/draft-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactName:       contact.name,
          company:           contact.company,
          title:             contact.title,
          lastActivity:      contact.lastActivity,
          lastActivityDate:  contact.lastActivityDate,
          notes:             contact.notes,
          prospectType:      contact.prospectType,
          priority:          contact.priority,
          recentActivities:  contact.recentActivities,
          guidance:          currentGuidance || '',
          brokerName,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSubject(data.subject || '');
      setBody(data.body || '');
    } catch (err) {
      setError('Could not generate draft — you can write your own below.');
      setSubject(`Following up — ${contact.name?.split(' ')[0] || ''}`);
      setBody('');
    } finally {
      setDrafting(false);
    }
  }, [contact, brokerName]);

  useEffect(() => {
    if (tokens) generateDraft('');
  }, []); // eslint-disable-line

  // ── Send email ────────────────────────────────────────────────────────────
  async function handleSend() {
    if (!subject.trim()) { setError('Please add a subject line.'); return; }
    if (!body.trim())    { setError('Please write something in the body.'); return; }
    if (!contact.email)  { setError('This contact has no email address on file.'); return; }

    setSending(true);
    setError(null);

    const currentTokens = getGmailTokens(brokerName);
    if (!currentTokens?.accessToken) {
      setError('Gmail not connected. Please go back and reconnect.');
      setSending(false);
      return;
    }

    try {
      const res = await fetch('/api/gmail-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken:  currentTokens.accessToken,
          refreshToken: currentTokens.refreshToken,
          brokerDisplayName: currentTokens.displayName || '',
          brokerEmail:       currentTokens.email       || '',
          signature:         currentTokens.signature   || '',
          to:      contact.email,
          subject: subject.trim(),
          body:    body.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to send email');
      }

      // Update tokens if they were refreshed
      if (data.newToken) {
        setGmailTokens(brokerName, {
          ...currentTokens,
          accessToken: data.newToken.accessToken,
          expiry:      data.newToken.expiry,
        });
      }

      // Log to Monday Activity Log (non-blocking)
      try {
        if (contact.contactItemId) {
          await createActivityLogEntry({
            contactItemId:       contact.contactItemId,
            prospectingItemName: contact.name,
            activityType:        'Email',
            outcome:             'Email sent',
            notes:               `Subject: ${subject.trim()}\n\n${body.trim()}`,
            nextFollowUpDate:    nextWeekStr(),
            stageAtLog:          '',
          });
        }
      } catch (logErr) {
        // Email sent successfully even if Monday log fails
        setLogError('Email sent! Note: Activity Log entry failed — please log manually in Monday.');
      }

      setSent(true);
      setTimeout(() => onSent(), 2000);
    } catch (err) {
      setError(err.message || 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  }

  // ── No Gmail connected ────────────────────────────────────────────────────
  if (!tokens) {
    return <ConnectGmail brokerName={brokerName} onCancel={onCancel} />;
  }

  // ── Sent state ────────────────────────────────────────────────────────────
  if (sent) {
    return (
      <div style={s.page}>
        <div style={s.nav}>
          <p style={s.navTitle}>Email</p>
        </div>
        <div style={{ ...s.body, justifyContent: 'center' }}>
          <div style={s.success}>
            ✅ Email sent to {contact.name}!<br />
            <span style={{ fontSize: '13px', fontWeight: '400' }}>Returning to contact…</span>
          </div>
          {logError && <div style={s.warning}>{logError}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.backBtn} onClick={onCancel}>←</button>
        <p style={s.navTitle}>Email {contact.name?.split(' ')[0]}</p>
        <span style={s.navSub}>
          {drafting
            ? <span style={s.draftingBadge}>✦ Drafting…</span>
            : contact.email}
        </span>
      </div>

      <div style={s.body}>
        {error && <div style={s.error}>{error}</div>}

        {/* To field */}
        <div style={s.card}>
          <label style={s.label}>To</label>
          <div style={{
            padding: '10px 12px', borderRadius: '8px',
            backgroundColor: C.bgSoft, fontSize: '14px',
            color: contact.email ? C.textPrimary : C.textTertiary,
            fontStyle: contact.email ? 'normal' : 'italic',
          }}>
            {contact.email || 'No email on file for this contact'}
          </div>
        </div>

        {/* Subject */}
        <div style={s.card}>
          <label style={s.label}>Subject</label>
          {drafting
            ? <div style={{ ...s.input, color: C.textTertiary, fontStyle: 'italic' }}>Generating…</div>
            : (
              <input
                style={s.input}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            )
          }
        </div>

        {/* AI Guidance */}
        <div style={s.card}>
          <label style={s.label}>Guidance for AI</label>
          <textarea
            style={{ ...s.textarea, minHeight: '72px' }}
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder="Optional: guide the tone, focus, or context — e.g. 'Keep it short and warm. She mentioned she's looking for flex space around 3,000 sq ft.' After generating, type 'make it shorter' or 'be more direct' and regenerate."
            disabled={drafting}
          />
          <button
            style={{ ...s.btnIndigo, marginTop: '10px', width: '100%' }}
            onClick={() => generateDraft(guidance)}
            disabled={drafting}
          >
            {drafting ? '✦ Writing…' : '✦ Generate Draft'}
          </button>
        </div>

        {/* Body */}
        <div style={s.card}>
          <label style={{ ...s.label, marginBottom: '8px' }}>Email Body</label>
          {drafting
            ? (
              <div style={{ ...s.textarea, color: C.textTertiary, fontStyle: 'italic', display: 'flex', alignItems: 'flex-start', paddingTop: '12px' }}>
                Writing your draft…
              </div>
            )
            : (
              <textarea
                style={s.textarea}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Your email will appear here after generating, or write your own."
              />
            )
          }
        </div>

        {/* Actions */}
        <button
          style={{
            ...s.btnPrimary,
            backgroundColor: (sending || drafting || !contact.email) ? '#9CA3AF' : C.teal,
            cursor: (sending || drafting || !contact.email) ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSend}
          disabled={sending || drafting || !contact.email}
        >
          {sending ? 'Sending…' : `Send to ${contact.email || 'no email on file'}`}
        </button>

        <button style={s.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
